import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import _noop from 'lodash/noop';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

import { mainCq } from '../../../styles/helpers/main-cq';
import { pageTemplate } from '../../../styles/theme';

import { FavoriteTeamCardV2 } from '../../FavoriteTeamCard';
import TeamCategoryCard from '../../TeamCategoryCard';

import { CardType } from './constants';

const Team = styled(FavoriteTeamCardV2)`
    grid-column: 1 / 5;

    ${mainCq.sm`
        grid-column: 1 / 2;
        grid-row: 1 / 3;
    `}

    ${mainCq.md`
        grid-column: 1 / 2;
        grid-row: 1 / 2;
    `}
`;

const GAP = 8;
const TEAM_FRAGMENT = 401;
const CATEGORY_FRAGMENT_SM = 68.42;
const CATEGORY_FRAGMENT_MD = 145;
const CATEGORY_COLUMNS_SM = 2;
const CATEGORY_COLUMNS_MD = 4;
const Wrapper = styled.div`
    display: grid;
    gap: ${GAP}px;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: auto;
    grid-auto-rows: 1fr;

    ${mainCq.sm`
        grid-template-columns: ${TEAM_FRAGMENT}fr ${`${CATEGORY_FRAGMENT_SM}fr `.repeat(CATEGORY_COLUMNS_SM)};
    `}

    ${mainCq.md`
        grid-template-columns: ${TEAM_FRAGMENT}fr ${`${CATEGORY_FRAGMENT_MD}fr `.repeat(CATEGORY_COLUMNS_MD)};
    `}
`;

/**
 * img sizes > SM width calculation
 *
 * FIXED_PX_SPACES = 2 * pageMargins + GAP_SUM
 * FRAGMENT_PERCENT = (TEAM_FRAGMENT * 100)/FRAGMENTS_SUM
 * FRAGMENT_WIDTH = calc(((100vw - FIXED_PX_SPACES) * FRAGMENT_PERCENT) / 100)
 */
const FIXED_PX_SPACES_SM = parseFloat(pageTemplate.paddingLeftRight.xs) * 2 + CATEGORY_COLUMNS_SM * GAP;
const FIXED_PX_SPACES_MD = parseFloat(pageTemplate.paddingLeftRight.md) * 2 + CATEGORY_COLUMNS_MD * GAP;
const FIXED_PX_SPACES_LG = parseFloat(pageTemplate.paddingLeftRight.lg) * 2 + CATEGORY_COLUMNS_MD * GAP;
const FRAGMENT_PERCENT_SM = (TEAM_FRAGMENT * 100)/(TEAM_FRAGMENT + CATEGORY_COLUMNS_SM * CATEGORY_FRAGMENT_SM);
const FRAGMENT_PERCENT_MD = (TEAM_FRAGMENT * 100)/(TEAM_FRAGMENT + CATEGORY_COLUMNS_MD * CATEGORY_FRAGMENT_MD);
const FRAGMENT_WIDTH_SM = `calc(((100vw - ${FIXED_PX_SPACES_SM}px) * ${FRAGMENT_PERCENT_SM}) / 100)`;
const FRAGMENT_WIDTH_MD = `calc(((100vw - ${FIXED_PX_SPACES_MD}px) * ${FRAGMENT_PERCENT_MD}) / 100)`;
const FRAGMENT_WIDTH_LG = `calc(((100vw - ${FIXED_PX_SPACES_LG}px) * ${FRAGMENT_PERCENT_MD}) / 100)`;
const teamImgSizes = [
    `(min-width: ${BREAKPOINTS.lg}) ${FRAGMENT_WIDTH_LG},`,
    `(min-width: ${BREAKPOINTS.md}) ${FRAGMENT_WIDTH_MD},`,
    `(min-width: ${BREAKPOINTS.sm}) ${FRAGMENT_WIDTH_SM},`,
    `calc(100vw - ${(parseFloat(pageTemplate.paddingLeftRight.xs) * 2)}px)`,
].join(' ');

const PersonalizedModule = (props) => {

    const {
        teamId,
        teamName,
        teamLink,
        teamImageTemplateUrl,
        categories,
        loading,
        onItemClick,
    } = props;

    return (
        <Wrapper>
            {loading ? (
                <>
                    <Team loading />
                    {Array.from({length: 4}, (_, index) => <TeamCategoryCard key={index} loading />)}
                </>
            ) : (
                <>
                    <Team
                        forwardedAs={Link}
                        to={teamLink}
                        id={teamId}
                        name={teamName}
                        imageTemplateUrl={teamImageTemplateUrl}
                        imgSizes={teamImgSizes}
                        onClick={() => onItemClick({
                            id: teamName,
                            type: CardType.TEAM,
                        })}
                    />
                    {categories.map((props, index) => (
                        <TeamCategoryCard
                            key={`category-${index}`}
                            as={Link}
                            {...props}
                            onClick={() => onItemClick({
                                id: props.category,
                                index,
                                type: CardType.CATEGORY,
                            })}
                            />
                        )
                    )}
                </>
            )}
        </Wrapper>
    );

};

PersonalizedModule.propTypes = {
    teamId: PropTypes.number,
    teamName: PropTypes.string,
    teamLink: PropTypes.string,
    teamImageTemplateUrl: PropTypes.string,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            category: PropTypes.string.isRequired,
            iconName: PropTypes.string.isRequired,
            to: PropTypes.string.isRequired,
        })
    ),
    loading: PropTypes.bool,
    onItemClick: PropTypes.func,
};

PersonalizedModule.defaultProps = {
    loading: false,
    onItemClick: _noop,
};

export default memo(PersonalizedModule);
